import { useRef, useState, useEffect, useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../ui/tabs';
import { Package, AlertCircle, FileText, CheckCircle, Plus, Download, Filter, ArrowUp, ArrowDown, ArrowUpDown, Loader2 } from 'lucide-react';
import { POHeadersFiltersPanel } from './POHeadersFiltersPanel';
import { PurchaseOrder, purchasingAPI } from '../../../lib/api';
import { Pagination } from '../../ui/pagination';
import { useFormatters } from '../../../contexts/PreferencesContext';
import { useView } from '../../../contexts/ViewContext';
import { toast } from 'sonner';

interface POHeadersTabProps {
  orders: PurchaseOrder[];
  loading: boolean;
  totalCount: number;
  currentPage: number;
  totalPages: number;
  onPageChange: (page: number) => void;
  onRowClick: (poId: number) => void;
  onPONumberClick: (poNumber: string) => void;
  textInputs: {
    poNumber: string;
    customer: string;
    supplier: string;
  };
  filters: {
    poNumber: string;
    customer: string;
    supplier: string;
    status: string;
    dateFrom: string;
    dateTo: string;
    site: string;
    customerGroup: string;
    commodityGroup: string;
    commodityType: string;
    pdfAttached: string;
    strategic: string;
  };
  siteOptions?: { value: string; label: string }[];
  customerGroupOptions?: { value: string; label: string }[];
  commodityGroupOptions?: { value: string; label: string }[];
  commodityTypeOptions?: { value: string; label: string }[];
  onFilterChange: (key: string, value: string) => void;
  onClearFilters: () => void;
  showFilters: boolean;
  activeTab: string;
  onTabChange: (tab: string) => void;
  onRefresh?: () => void;
  onSearch?: () => void;
  onToggleFilters?: () => void;
  tabCounts?: Record<string, number>;
  openOrderValue?: number;
  sortField?: string;
  sortOrder?: 'asc' | 'desc';
  onSort?: (field: string) => void;
}

export default function POHeadersTab({
  orders,
  loading: _loading,
  totalCount,
  currentPage,
  totalPages,
  onPageChange,
  onRowClick,
  onPONumberClick,
  textInputs,
  filters,
  siteOptions = [],
  customerGroupOptions = [],
  commodityGroupOptions = [],
  commodityTypeOptions = [],
  onFilterChange,
  onClearFilters,
  showFilters,
  activeTab,
  onTabChange,
  onRefresh,
  onSearch,
  onToggleFilters,
  tabCounts = {},
  openOrderValue = 0,
  sortField = '-issue_date',
  sortOrder: _sortOrder = 'desc',
  onSort,
}: POHeadersTabProps) {
  const { formatDate: contextFormatDate, formatCurrency, formatCurrencyFromBase } = useFormatters();
  // CR-4577: Filter site options by view context
  const { viewLevel, selectedCompanyId, selectedGroupId, getViewQueryParams } = useView();
  const viewScopedSites = useMemo(() => {
    if (viewLevel === 'SITE' && selectedCompanyId) return siteOptions.filter(s => s.value === String(selectedCompanyId));
    if (viewLevel === 'COMPANY' && selectedCompanyId) return siteOptions.filter(s => s.value === String(selectedCompanyId));
    return siteOptions;
  }, [siteOptions, viewLevel, selectedCompanyId, selectedGroupId]);

  const SortHeader = ({ field, label, align = 'left' }: { field: string; label: string; align?: string }) => {
    const active = sortField === field || sortField === `-${field}`;
    const isDesc = sortField.startsWith('-');
    return (
      <th
        className={`text-${align} py-3 px-3 font-medium cursor-pointer hover:bg-muted/50 select-none`}
        onClick={() => onSort?.(field)}
      >
        <span className="inline-flex items-center gap-1">
          {label}
          {active ? (
            isDesc ? <ArrowDown className="h-3 w-3" /> : <ArrowUp className="h-3 w-3" />
          ) : (
            <ArrowUpDown className="h-3 w-3 text-muted-foreground/50" />
          )}
        </span>
      </th>
    );
  };
  const pdfInputRef = useRef<HTMLInputElement>(null);
  const certInputRef = useRef<HTMLInputElement>(null);
  const uploadTargetRef = useRef<{ poId: number; type: 'po_pdf' | 'material_cert_supplier' } | null>(null);
  const [allCustomers, setAllCustomers] = useState<{ id: number; name: string }[]>([]);
  const [exporting, setExporting] = useState(false);

  // Load customers once from PO lines filter options (accessible to all users)
  useEffect(() => {
    purchasingAPI.getLineFilterOptions().then(res => {
      setAllCustomers(res.data.customers || []);
    }).catch(() => {});
  }, []);

  /**
   * Export PO headers across ALL pages, respecting the active tab + filters
   * + view-context (top-bar site/group/company). Mirrors the param-building
   * pattern in Purchasing.tsx fetchData() so the export matches what the
   * user sees in the table.
   */
  const handleExport = async (format: 'csv' | 'xlsx' = 'csv') => {
    try {
      setExporting(true);

      const queryParams: Record<string, string | number | boolean> = {
        file_format: format,
      };

      // Individual field filters using Django filter syntax (mirrors fetchData)
      if (filters.poNumber) {
        queryParams['po_number__icontains'] = filters.poNumber;
      }
      if (filters.customer) {
        queryParams['customer__name__icontains'] = filters.customer
          .replace(/[.\-_+]/g, ' ').replace(/\s+/g, ' ').trim();
      }
      if (filters.supplier) {
        queryParams['supplier__name__icontains'] = filters.supplier;
      }

      // Status filter from dropdown takes precedence over active tab
      if (filters.status) {
        queryParams['status'] = filters.status;
      } else if (activeTab !== 'all') {
        if (activeTab === 'open') {
          queryParams['status__in'] = 'sent,acknowledged,in_progress';
        } else if (activeTab === 'delivered') {
          queryParams['status'] = 'delivered';
        } else if (activeTab === 'closed') {
          queryParams['status'] = 'closed';
        }
      }

      // Date filters (issue_date)
      if (filters.dateFrom) {
        queryParams['issue_date__gte'] = filters.dateFrom;
      }
      if (filters.dateTo) {
        queryParams['issue_date__lte'] = filters.dateTo;
      }

      // PO Header filters
      if (filters.site) {
        queryParams['supplier'] = filters.site;
      }
      if (filters.customerGroup) {
        queryParams['customer__group'] = filters.customerGroup;
      }
      if (filters.commodityGroup) {
        queryParams['lines__commodity_group'] = filters.commodityGroup;
      }
      if (filters.commodityType) {
        queryParams['lines__commodity_type'] = filters.commodityType;
      }

      // View context (site / group / company) — without these the export
      // pulls everything the user has access to, ignoring the top-bar
      // site selector. Upstream's null-guard added for safety.
      const viewParams = getViewQueryParams();
      Object.entries(viewParams).forEach(([key, value]) => {
        if (value !== undefined && value !== null) queryParams[key] = value;
      });

      const response = await purchasingAPI.exportOrders(queryParams);
      const blob = new Blob([response.data], {
        type: format === 'xlsx'
          ? 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
          : 'text/csv',
      });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `po_headers_export_${new Date().toISOString().split('T')[0]}.${format}`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      toast.success('Exported sales orders');
    } catch (err: unknown) {
      console.error('Export failed:', err);
      const message = err instanceof Error ? err.message : 'Export failed';
      toast.error(`Failed to export sales orders: ${message}`);
    } finally {
      setExporting(false);
    }
  };

  const formatDate = (dateString: string) => {
    if (!dateString) return '-';
    return contextFormatDate(dateString);
  };

  const hasOnHoldLines = (order: PurchaseOrder) => {
    return order.lines?.some(line => line.on_hold) || false;
  };

  const hasPDFAttached = (order: PurchaseOrder) => {
    return order.documents?.some(doc => doc.document_type === 'po_pdf' || doc.file_name?.endsWith('.pdf')) || false;
  };

  const hasMaterialCert = (order: PurchaseOrder) => {
    return order.documents?.some(doc =>
      doc.document_type === 'material_cert_supplier' ||
      doc.document_type === 'certificate' ||
      doc.file_name?.toLowerCase().includes('cert')
    ) || false;
  };

  const handleUploadClick = (e: React.MouseEvent, poId: number, type: 'po_pdf' | 'material_cert_supplier') => {
    e.stopPropagation();
    uploadTargetRef.current = { poId, type };
    if (type === 'po_pdf') {
      pdfInputRef.current?.click();
    } else {
      certInputRef.current?.click();
    }
  };

  const handleFileSelected = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !uploadTargetRef.current) return;

    const { poId, type } = uploadTargetRef.current;
    const formData = new FormData();
    formData.append('file', file);
    formData.append('po', String(poId));
    formData.append('document_type', type);
    formData.append('source', 'supplier');

    try {
      await purchasingAPI.uploadPODocument(formData);
      toast.success(`${type === 'po_pdf' ? 'PDF' : 'Certificate'} uploaded successfully`);
      onRefresh?.();
    } catch (err: unknown) {
      console.error('Upload failed:', err);
      toast.error('Failed to upload document');
    }

    // Reset input
    e.target.value = '';
    uploadTargetRef.current = null;
  };

  const activeFilterCount = Object.entries(filters).filter(([key, val]) =>
    val && !['poNumber', 'customer', 'supplier'].includes(key)
  ).length + (textInputs.poNumber ? 1 : 0) + (textInputs.customer ? 1 : 0) + (textInputs.supplier ? 1 : 0);

  return (
    <div className="space-y-4">
      {/* Hidden file inputs for upload */}
      <input ref={pdfInputRef} type="file" accept=".pdf" className="hidden" onChange={handleFileSelected} />
      <input ref={certInputRef} type="file" accept=".pdf,.jpg,.jpeg,.png" className="hidden" onChange={handleFileSelected} />

      {/* Filters Panel */}
      {showFilters && (
        <POHeadersFiltersPanel
          filters={{
            site: filters.site,
            customerGroup: filters.customerGroup,
            customer: filters.customer,
            commodityGroup: filters.commodityGroup,
            commodityType: filters.commodityType,
            pdfAttached: filters.pdfAttached,
          }}
          poNumber={textInputs.poNumber}
          siteOptions={viewScopedSites}
          customerGroupOptions={customerGroupOptions}
          customerOptions={allCustomers.map((s) => ({ value: s.name, label: s.name }))}
          commodityGroupOptions={commodityGroupOptions}
          commodityTypeOptions={commodityTypeOptions}
          activeFilterCount={activeFilterCount}
          onFilterChange={onFilterChange}
          onSearch={onSearch}
          onClearFilters={onClearFilters}
          onHideFilters={onToggleFilters}
        />
      )}

      {/* Orders Table */}
      <Card>
        <Tabs value={activeTab} onValueChange={onTabChange}>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle>Sales Order Headers</CardTitle>
                <div className="flex items-center gap-4 mt-1">
                  <p className="text-sm text-muted-foreground">
                    Showing {orders.length} of {totalCount.toLocaleString()} orders
                  </p>
                  {/* Open Order Value is sent from the API in GBP base currency.
                      formatCurrencyFromBase converts it into the user's preferred
                      currency (customer sees their trading currency, supplier
                      sees their base currency). Using a hardcoded £ here was
                      mislabelling PLN-converted values for non-GBP customers. */}
                  {openOrderValue > 0 && (
                    <span className="text-sm font-medium text-primary">
                      Open Order Value: {formatCurrencyFromBase(openOrderValue)}
                    </span>
                  )}
                </div>
              </div>
              <div className="flex items-center gap-3">
                <button
                  onClick={onToggleFilters}
                  className={`px-3 py-1.5 rounded-lg border text-sm flex items-center gap-2 ${
                    showFilters ? 'bg-primary text-primary-foreground' : 'hover:bg-muted'
                  }`}
                >
                  <Filter className="h-3 w-3" />
                  {showFilters ? 'Hide Filters' : 'Filters'}
                </button>
                <button
                  onClick={() => handleExport('csv')}
                  disabled={exporting || totalCount === 0}
                  className="px-3 py-1.5 rounded-lg border text-sm flex items-center gap-2 hover:bg-muted disabled:opacity-50 disabled:cursor-not-allowed"
                  title="Export all matching sales orders to CSV"
                >
                  {exporting ? (
                    <Loader2 className="h-3 w-3 animate-spin" />
                  ) : (
                    <Download className="h-3 w-3" />
                  )}
                  {exporting ? 'Exporting…' : 'Export'}
                </button>
              </div>
            </div>
            <div className="flex items-center justify-between mt-3">
              <TabsList>
                <TabsTrigger value="all">
                  All Orders{tabCounts['all'] !== undefined ? ` (${tabCounts['all'].toLocaleString()})` : ''}
                </TabsTrigger>
                <TabsTrigger value="open">
                  Open{tabCounts['open'] !== undefined ? ` (${tabCounts['open'].toLocaleString()})` : ''}
                </TabsTrigger>
                <TabsTrigger value="closed">
                  Closed{tabCounts['closed'] !== undefined ? ` (${tabCounts['closed'].toLocaleString()})` : ''}
                </TabsTrigger>
              </TabsList>
            </div>
          </CardHeader>

          <TabsContent value={activeTab} className="m-0">
            <CardContent>
              {orders.length === 0 ? (
                <div className="text-center py-12">
                  <Package className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                  <p className="text-muted-foreground">
                    {textInputs.poNumber || textInputs.customer || textInputs.supplier || filters.status || filters.dateFrom || filters.dateTo
                      ? 'No sales orders match your filters'
                      : 'No sales orders found'}
                  </p>
                  {(textInputs.poNumber || textInputs.customer || textInputs.supplier || filters.status || filters.dateFrom || filters.dateTo) && (
                    <button
                      onClick={onClearFilters}
                      className="mt-4 text-sm text-primary hover:underline"
                    >
                      Clear filters
                    </button>
                  )}
                </div>
              ) : (
                <>
                  {/* Top Pagination */}
                  <Pagination
                    currentPage={currentPage}
                    totalPages={totalPages}
                    totalCount={totalCount}
                    itemsPerPage={50}
                    onPageChange={onPageChange}
                    itemLabel="orders"
                  />

                  {/* CR-3866: Added max-height and sticky header support */}
                  <div className="overflow-x-auto overflow-y-auto max-h-[calc(100vh-350px)]">
                    <table className="w-full text-sm">
                      <thead className="sticky top-0 z-20">
                        <tr className="border-b bg-muted/80 backdrop-blur-sm">
                          <SortHeader field="po_number" label="SO Number" />
                          <SortHeader field="status" label="Status" />
                          <SortHeader field="supplier__name" label="Supplier" />
                          <th className="text-left py-3 px-3 font-medium">Customer Group</th>
                          <SortHeader field="customer__name" label="Customer" />
                          <SortHeader field="total_value" label="Total Value" align="right" />
                          <SortHeader field="line_count" label="Lines" align="center" />
                          <SortHeader field="open_line_count" label="Open" align="center" />
                          <SortHeader field="closed_line_count" label="Closed" align="center" />
                          <th className="text-center py-3 px-3 font-medium">PDF</th>
                          <th className="text-center py-3 px-3 font-medium">Certs</th>
                          <SortHeader field="issue_date" label="Issue Date" />
                          <th className="text-left py-3 px-3 font-medium">Created By</th>
                        </tr>
                      </thead>
                      <tbody>
                        {orders.map((order) => (
                            <tr
                              key={order.id}
                              onClick={() => onRowClick(order.id)}
                              className={`border-b hover:bg-muted/50 transition-colors cursor-pointer ${order.status === 'closed' ? 'opacity-50 bg-muted/30' : ''}`}
                            >
                              {/* PO Number */}
                              <td className="py-3 px-3">
                                <div className="flex items-center gap-2">
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      onPONumberClick(order.po_number);
                                    }}
                                    className="font-medium text-primary hover:underline"
                                  >
                                    {order.po_number}
                                  </button>
                                  {hasOnHoldLines(order) && (
                                    <div title="Has lines on hold">
                                      <AlertCircle className="h-4 w-4 text-red-500" />
                                    </div>
                                  )}
                                </div>
                              </td>
                              {/* Status */}
                              <td className="py-3 px-3">
                                <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                                  order.status === 'closed'
                                    ? 'bg-slate-100 text-slate-800'
                                    : 'bg-green-100 text-green-800'
                                }`}>
                                  {order.status === 'closed' ? 'Closed' : 'Open'}
                                </span>
                              </td>
                              {/* Supplier */}
                              <td className="py-3 px-3">
                                {order.supplier_name || order.supplier_name || '-'}
                              </td>
                              {/* Customer Group */}
                              <td className="py-3 px-3">
                                {order.customer_group_name || '-'}
                              </td>
                              {/* Customer */}
                              <td className="py-3 px-3">
                                <a
                                  href={`/customers/${order.customer}`}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  onClick={(e) => e.stopPropagation()}
                                  className="text-primary hover:underline"
                                >
                                  {order.customer_name}
                                </a>
                              </td>
                              {/* Total Value */}
                              <td className="py-3 px-3 text-right">
                                {formatCurrency(parseFloat(order.total_value?.toString() || '0'), order.currency, 3)}
                              </td>
                              {/* Line Counts */}
                              <td className="py-3 px-3 text-center text-sm">{order.line_count ?? '-'}</td>
                              <td className="py-3 px-3 text-center text-sm">
                                {order.open_line_count ? (
                                  <span className="text-orange-600 font-medium">{order.open_line_count}</span>
                                ) : '-'}
                              </td>
                              <td className="py-3 px-3 text-center text-sm">
                                {order.closed_line_count ? (
                                  <span className="text-green-600">{order.closed_line_count}</span>
                                ) : '-'}
                              </td>
                              {/* PDF - clickable for upload */}
                              <td className="py-3 px-3 text-center">
                                {hasPDFAttached(order) ? (
                                  <div className="flex justify-center" title="PDF attached">
                                    <FileText className="h-4 w-4 text-green-600" />
                                  </div>
                                ) : (
                                  <button
                                    onClick={(e) => handleUploadClick(e, order.id, 'po_pdf')}
                                    className="flex justify-center mx-auto hover:bg-muted rounded p-1 transition-colors"
                                    title="Upload PDF"
                                  >
                                    <Plus className="h-4 w-4 text-gray-400 hover:text-primary" />
                                  </button>
                                )}
                              </td>
                              {/* Material Certs - clickable for upload */}
                              <td className="py-3 px-3 text-center">
                                {hasMaterialCert(order) ? (
                                  <div className="flex justify-center" title="Material certificate attached">
                                    <CheckCircle className="h-4 w-4 text-green-600" />
                                  </div>
                                ) : (
                                  <button
                                    onClick={(e) => handleUploadClick(e, order.id, 'material_cert_supplier')}
                                    className="flex justify-center mx-auto hover:bg-muted rounded p-1 transition-colors"
                                    title="Upload Certificate"
                                  >
                                    <Plus className="h-4 w-4 text-gray-400 hover:text-primary" />
                                  </button>
                                )}
                              </td>
                              {/* Issue Date */}
                              <td className="py-3 px-3">
                                {formatDate(order.issue_date)}
                              </td>
                              {/* Created By */}
                              <td className="py-3 px-3">
                                {order.created_by_name || '-'}
                              </td>
                            </tr>
                          ))}
                      </tbody>
                    </table>
                  </div>

                  {/* Bottom Pagination */}
                  <Pagination
                    currentPage={currentPage}
                    totalPages={totalPages}
                    totalCount={totalCount}
                    itemsPerPage={50}
                    onPageChange={onPageChange}
                    itemLabel="orders"
                  />
                </>
              )}
            </CardContent>
          </TabsContent>
        </Tabs>
      </Card>
    </div>
  );
}
